import { useState, useEffect, useRef } from 'react';
import { useDataChannel } from '@livekit/components-react';

// Video backgrounds with fallbacks
const DEFAULT_BACKGROUNDS = [
  {
    id: 'off',
    name: 'Off',
    type: 'none',
    playAudio: false
  },
  {
    id: 'floating-market',
    name: 'Floating Market',
    url: '/videos/optimized/floating-market.mp4',
    fallbackUrl: '/videos/shutterstock_1078380857.mov',
    type: 'video',
    playAudio: false
  },
  {
    id: 'urban',
    name: 'Urban',
    url: '/videos/optimized/urban.mp4',
    fallbackUrl: '/videos/shutterstock_1056217589.mov',
    type: 'video',
    playAudio: false
  },
  {
    id: 'forest',
    name: 'Forest',
    url: '/videos/optimized/forest.mp4',
    fallbackUrl: '/videos/shutterstock_1060614142.mp4',
    type: 'video',
    playAudio: false
  },
  {
    id: 'beach',
    name: 'Beach',
    url: '/videos/optimized/beach.mp4',
    fallbackUrl: '/videos/shutterstock_1065107863.mp4',
    type: 'video',
    playAudio: false
  },
  {
    id: 'piano-concerto',
    name: 'Piano Concerto',
    url: '/videos/optimized/piano-concerto-lowres.mp4',
    fallbackUrl: '/videos/optimized/piano-concerto-5.mp4',
    type: 'video',
    playAudio: true
  },
  {
    id: 'fireworks',
    name: 'New Year Fireworks',
    url: '/videos/optimized/new-year-fireworks-in-the-world.webm',
    fallbackUrl: '/videos/optimized/new-year-fireworks-in-the-world.webm',
    type: 'video',
    playAudio: false
  },
  {
    id: 'fur-elise',
    name: 'Für Elise',
    url: '/videos/optimized/BeethovenFurElise.webm',
    fallbackUrl: '/videos/optimized/BeethovenFurElise.webm',
    type: 'video',
    playAudio: true
  }
];

// Helper function to get the optimal video format based on browser support
const getOptimalVideoFormat = (url) => {
  // Extract the base URL without extension
  const baseUrl = url.substring(0, url.lastIndexOf('.')) || url;
  
  // Check if browser supports WebM
  const supportsWebM = document.createElement('video').canPlayType('video/webm') !== '';
  
  // Return appropriate format
  return `${baseUrl}.${supportsWebM ? 'webm' : 'mp4'}`;
};

export default function SharedBackground({ roomName, isOpen, onClose, defaultBackgroundId = 'off', onBackgroundChange, onAudioStateChange, isAudioControlsVisible: externalAudioControlsVisible, isMuted: externalMuted }) {
  // Find the default background based on the provided ID or use the first one
  const defaultBackground = DEFAULT_BACKGROUNDS.find(bg => bg.id === defaultBackgroundId) || DEFAULT_BACKGROUNDS[0];
  
  const [selectedBackground, setSelectedBackground] = useState(defaultBackground);
  const [isBackgroundSelectorOpen, setIsBackgroundSelectorOpen] = useState(false);
  // Use external control for audio controls visibility if provided, otherwise use internal state
  const [internalAudioControlsVisible, setInternalAudioControlsVisible] = useState(false);
  const isAudioControlsVisible = externalAudioControlsVisible !== undefined ? externalAudioControlsVisible : internalAudioControlsVisible;
  const videoRef = useRef(null);

  // Audio state: start muted for compatibility with autoplay policies
  const [audioState, setAudioState] = useState({
    hasAudio: true,
    isMuted: true,
  });
  
  // Handle external control of the background selector
  useEffect(() => {
    if (isOpen !== undefined) {
      setIsBackgroundSelectorOpen(isOpen);
    }
  }, [isOpen]);
  
  // Use LiveKit's data channel with a specific topic for background synchronization
  // This will filter messages by the background topic
  const backgroundTopic = `background-${roomName}`;
  const { send, message } = useDataChannel(backgroundTopic);
  
  // Log when component mounts to verify channel name
  useEffect(() => {
    console.log(`SharedBackground mounted for room: ${roomName}, using background topic: ${backgroundTopic}`);
  }, [roomName, backgroundTopic]);

  // Handle incoming background change messages
  useEffect(() => {
    if (!message) return;
    
    console.log('Received message on background channel:', message);
    
    try {
      const decoded = new TextDecoder().decode(message.payload);
      const data = JSON.parse(decoded);
      
      console.log('Parsed message data:', data);
      
      if (data && data.type === 'background-change') {
        console.log('Background change requested to:', data.backgroundId);
        const background = DEFAULT_BACKGROUNDS.find(bg => bg.id === data.backgroundId);
        if (background) {
          console.log('Setting background to:', background.name);
          setSelectedBackground(background);
        } else {
          console.warn('Background not found with id:', data.backgroundId);
        }
      }
    } catch (error) {
      console.error('Error parsing background message:', error);
    }
  }, [message]);

  // Toggle audio controls visibility
  const toggleAudioControls = () => {
    setInternalAudioControlsVisible(!internalAudioControlsVisible);
  };
  
  // Toggle audio mute state
  const toggleMute = () => {
    if (videoRef.current) {
      // If external mute state is provided, use it instead of the video element's muted state
      if (externalMuted !== undefined) {
        videoRef.current.muted = externalMuted;
      } else {
        videoRef.current.muted = !videoRef.current.muted;
        // Notify parent component about audio state change
        if (onAudioStateChange) {
          onAudioStateChange({
            hasAudio: selectedBackground.playAudio,
            isMuted: videoRef.current.muted
          });
        }
      }
    }
  };

  // Reset audio controls when background changes and notify about audio state
  useEffect(() => {
    setInternalAudioControlsVisible(false);
    
    // Notify parent about audio state when background changes
    if (onAudioStateChange && selectedBackground) {
      onAudioStateChange({
        hasAudio: selectedBackground.playAudio,
        isMuted: videoRef.current?.muted || !selectedBackground.playAudio
      });
    }
  }, [selectedBackground]); // Removed onAudioStateChange from dependencies to prevent infinite loop

  // Update video element when background changes or external mute state changes
  useEffect(() => {
    if (videoRef.current && selectedBackground.type === 'video') {
      // If external mute state is provided, use it, otherwise use the playAudio property
      if (externalMuted !== undefined) {
        videoRef.current.muted = externalMuted;
      } else {
        videoRef.current.muted = !selectedBackground.playAudio;
      }
      
      // Autoplay the video (cancel-safe)
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch((e) => {
          if (e.name !== 'AbortError') {
            console.error('Error playing video:', e);
          }
        });
      }
    }
  }, [selectedBackground, externalMuted]);

  // Send background change to other participants
  const handleBackgroundChange = (background) => {
    console.log('User selected background:', background.name);
    setSelectedBackground(background);
    setIsBackgroundSelectorOpen(false);
    setInternalAudioControlsVisible(false);
    //setIsAudioControlsVisible(false); // Hide audio controls when background changes
    
    // Notify parent component about the background change
    if (onBackgroundChange) {
      onBackgroundChange(background.id === 'off' ? false : true, background.id);
    }
    
    // Notify parent component if onClose callback is provided
    if (onClose) {
      onClose();
    }
    
    // Notify other participants about the background change
    const message = {
      type: 'background-change',
      backgroundId: background.id
    };
    
    console.log('Sending background change message:', message);
    try {
      const encodedMessage = new TextEncoder().encode(JSON.stringify(message));
      send(encodedMessage);
      console.log('Message sent successfully');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  return (
    <>
      {/* Video Background */}
      <div className="shared-background">
        {selectedBackground.type === 'none' ? null : selectedBackground.type === 'video' && (
          <>
            <video
              key={selectedBackground.id}
              ref={videoRef}
              src={selectedBackground.url}
              autoPlay
              loop
              muted={audioState.isMuted}
              playsInline
              preload="auto"
              controls={false} /* We'll use our custom controls instead */
              disablePictureInPicture
              disableRemotePlayback
              onError={(e) => {
                console.log(`Error loading video: ${selectedBackground.url}`, e);
                // Try fallback if available
                if (selectedBackground.fallbackUrl) {
                  console.log(`Trying fallback: ${selectedBackground.fallbackUrl}`);
                  e.target.src = selectedBackground.fallbackUrl;
                }
              }}
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                zIndex: 0,
                willChange: 'transform',
                transform: 'translateZ(0)'
              }}
            />
          </>
        )}
        {selectedBackground.type === 'image' && (
          <img
            src={selectedBackground.url}
            alt="Background"
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: 0,
              willChange: 'transform',
              transform: 'translateZ(0)'
            }}
          />
        )}
      </div>

      {/* Audio Controls Panel - Only shown when audio controls are visible, now top-level */}
      {selectedBackground.type === 'video' && selectedBackground.playAudio && isAudioControlsVisible && (
        <div
          className="audio-control-panel"
          style={{
            position: 'absolute',
            bottom: '70px',  /* Position above the control bar */
            right: '520px',   /* Position on the right side */
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            borderRadius: '8px',
            padding: '10px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            width: '200px',
            boxShadow: '0 0 10px rgba(0, 0, 0, 0.5)',
            zIndex: 10
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <button
              onClick={toggleMute}
              style={{
                backgroundColor: 'transparent',
                color: 'white',
                border: 'none',
                cursor: 'pointer',
                fontSize: '20px',
                padding: '5px'
              }}
            >
              {videoRef.current?.muted ? '🔇' : '🔊'}
            </button>
            
            {/* Volume control using integers 0-100 with scaling factor for better slider behavior */}
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              defaultValue={Math.round((videoRef.current?.volume || 0.05) / 10)}
              onChange={(e) => {
                if (videoRef.current) {
                  // Convert slider value (0-10) to actual volume (0-0.10)
                  const volumeValue = parseInt(e.target.value) / 100;
                  console.log(`Setting volume to: ${volumeValue} (slider value: ${e.target.value})`);
                  videoRef.current.volume = volumeValue;
                  if (parseFloat(e.target.value) > 0) {
                    videoRef.current.muted = false;
                    // Notify parent about audio state change
                    if (onAudioStateChange) {
                      onAudioStateChange({
                        hasAudio: selectedBackground.playAudio,
                        isMuted: false
                      });
                    }
                  }
                }
              }}
              style={{ flex: 1, marginLeft: '10px' }}
            />
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <button
              onClick={() => {
                if (videoRef.current) {
                  if (videoRef.current.paused) {
                    videoRef.current.play();
                  } else {
                    videoRef.current.pause();
                  }
                }
              }}
              style={{
                backgroundColor: 'transparent',
                color: 'white',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                padding: '5px'
              }}
            >
              {videoRef.current?.paused ? '▶️ Play' : '⏸️ Pause'}
            </button>
            
            <button
              onClick={() => {
                if (videoRef.current) {
                  videoRef.current.currentTime = 0;
                  videoRef.current.play();
                }
              }}
              style={{
                backgroundColor: 'transparent',
                color: 'white',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                padding: '5px'
              }}
            >
              🔄 Restart
            </button>
          </div>
        </div>
      )}

      {/* Background Selector Button - Hidden as we're using the control panel button instead */}

      {/* Background Selector Panel */}
      {isBackgroundSelectorOpen && (
        <div 
          className="background-selector"
          style={{
            position: 'absolute',
            bottom: '80px',
            right: '20px',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            padding: '15px',
            borderRadius: '8px',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            maxWidth: '300px'
          }}
        >
          <h3 style={{ color: 'white', margin: '0 0 10px 0' }}>Select Background</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {DEFAULT_BACKGROUNDS.map((background) => (
              <div 
                key={background.id}
                onClick={() => handleBackgroundChange(background)}
                style={{
                  width: '120px',
                  height: '70px',
                  overflow: 'hidden',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  border: selectedBackground.id === background.id ? '2px solid #4a9eff' : '2px solid transparent'
                }}
              >
                {background.type === 'none' ? (
                  <div style={{ 
                    width: '100%', 
                    height: '100%', 
                    background: 'linear-gradient(135deg, #121212 0%, #1a1a2e 100%)', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '12px'
                  }}>
                    <span>No Background</span>
                  </div>
                ) : background.type === 'video' ? (
                  <video 
                    src={background.url} 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    muted
                  />
                ) : (
                  <img 
                    src={background.url}
                    alt={background.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                )}
                <div style={{ 
                  position: 'absolute', 
                  bottom: 0, 
                  left: 0, 
                  right: 0, 
                  backgroundColor: 'rgba(0,0,0,0.7)', 
                  padding: '2px 5px',
                  fontSize: '10px',
                  color: 'white'
                }}>
                  {background.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
